import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ListItemText, Menu, MenuItem } from '@bb-ui/react-library';
import { Checkbox } from '@bb-ui/react-library/dist/components/Checkbox';
import { PrimaryButton } from '@bb-ui/react-library/dist/components/Button';
import { LEARN_SITE_FILTER_KEYS, LearnSiteFilterKey } from './learnSiteFilters';

export interface LearnSitesFilterMenuProps {
  enabledFilters: ReadonlySet<LearnSiteFilterKey>;
  onToggleFilter: (key: LearnSiteFilterKey) => void;
}

/** The Filters button and its checklist, as on the Captain Learn list. */
export const LearnSitesFilterMenu: React.FunctionComponent<LearnSitesFilterMenuProps> = ({
  enabledFilters,
  onToggleFilter,
}) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement>();

  return (
    <>
      <PrimaryButton
        aria-controls={menuAnchor ? 'learn-sites-filter-menu' : undefined}
        aria-expanded={!!menuAnchor}
        aria-haspopup="menu"
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => setMenuAnchor(event.currentTarget)}
        data-testid="learn-sites-filters-button"
      >
        {t('learnSites.filters.button')}
        {enabledFilters.size > 0 ? ` (${enabledFilters.size})` : ''}
      </PrimaryButton>
      <Menu
        anchorEl={menuAnchor}
        id="learn-sites-filter-menu"
        onClose={() => setMenuAnchor(undefined)}
        open={!!menuAnchor}
        MenuListProps={{ 'aria-label': t('learnSites.filters.menuAriaLabel') }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        data-testid="learn-sites-filter-menu"
      >
        {LEARN_SITE_FILTER_KEYS.map((key) => {
          const checked = enabledFilters.has(key);
          return (
            <MenuItem
              key={key}
              role="menuitemcheckbox"
              aria-checked={checked}
              selected={checked}
              onClick={() => onToggleFilter(key)}
              data-testid={`learn-sites-filter-option-${key}`}
            >
              <Checkbox checked={checked} tabIndex={-1} inputProps={{ 'aria-hidden': true }} />
              <ListItemText primary={t(`learnSites.filters.${key}`)} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default LearnSitesFilterMenu;
